import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { X, ChatCircle } from "@phosphor-icons/react";

/**
 * Followers / Following modal.
 * Props:
 *   userId  - whose list to show
 *   kind    - "followers" | "following"
 *   onClose - ()=>void
 *   onChange - optional callback after a follow toggle (so parent can refresh counts)
 */
export default function FollowListModal({ userId, kind, onClose, onChange }) {
    const { t } = useTranslation();
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState({}); // per-id in-flight follow toggle

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/users/${userId}/${kind}`);
            setUsers(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [userId, kind]);

    useEffect(() => { load(); }, [load]);

    const toggleFollow = async (targetId) => {
        if (!me) { toast.error(t("nav.login")); return; }
        setWorking((w) => ({ ...w, [targetId]: true }));
        try {
            const { data } = await api.post("/follow", { user_id: targetId });
            setUsers((prev) => prev.map((u) => u.id === targetId ? { ...u, viewer_is_following: data.following } : u));
            onChange?.();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setWorking((w) => {
                const n = { ...w }; delete n[targetId]; return n;
            });
        }
    };

    const title = kind === "followers"
        ? (t("profile.followers") || "Followers")
        : (t("profile.following") || "Following");

    return (
        <div
            data-testid={`follow-list-${kind}`}
            className="fixed inset-0 z-[80] bg-black/80 flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={onClose}
        >
            <div
                className="bg-wta-surface border border-wta-border rounded-t-lg md:rounded-sm w-full max-w-md max-h-[85dvh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between px-4 py-3 border-b border-wta-border">
                    <h2 className="font-heading text-xl uppercase tracking-wider">
                        {title}
                        {!loading && <span className="ml-2 text-sm text-wta-muted font-normal">({users.length})</span>}
                    </h2>
                    <button data-testid="follow-list-close" onClick={onClose} className="text-wta-muted hover:text-white">
                        <X size={18} />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 text-center text-wta-muted text-sm">{t("common.loading")}</div>
                    ) : users.length === 0 ? (
                        <div className="p-12 text-center text-wta-muted text-sm">
                            {kind === "followers"
                                ? "No followers yet"
                                : "Not following anyone yet"}
                        </div>
                    ) : (
                        <ul className="divide-y divide-wta-border">
                            {users.map((u) => {
                                const isSelf = me?.id === u.id;
                                const isFollowing = !!u.viewer_is_following;
                                const busy = !!working[u.id];
                                return (
                                    <li key={u.id} data-testid={`follow-row-${u.name}`} className="flex items-center gap-3 px-4 py-3">
                                        <Link to={`/profile/${u.id}`} onClick={onClose} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80">
                                            <Avatar user={u} />
                                            <div className="min-w-0">
                                                <div className="font-heading text-sm tracking-wide truncate">@{u.name}</div>
                                                {u.bio && <div className="text-xs text-wta-muted truncate">{u.bio}</div>}
                                            </div>
                                        </Link>
                                        {!isSelf && me && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <Link
                                                    to={`/messages/${u.id}`}
                                                    onClick={onClose}
                                                    data-testid={`follow-row-message-${u.name}`}
                                                    className="btn-ghost text-xs p-2 rounded-sm"
                                                    title={t("messages.title")}
                                                >
                                                    <ChatCircle size={14} />
                                                </Link>
                                                <button
                                                    onClick={() => toggleFollow(u.id)}
                                                    disabled={busy}
                                                    data-testid={`follow-row-toggle-${u.name}`}
                                                    className={`${isFollowing ? "btn-ghost" : "btn-primary"} px-3 py-1.5 rounded-sm text-xs font-heading uppercase tracking-wider min-w-[88px] disabled:opacity-50`}
                                                >
                                                    {busy
                                                        ? "…"
                                                        : isFollowing
                                                            ? t("feed.unfollow")
                                                            : t("feed.follow")}
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function Avatar({ user }) {
    return (
        <div className="w-10 h-10 rounded-full bg-wta-surface2 border border-wta-border flex items-center justify-center font-heading text-sm overflow-hidden shrink-0">
            {user?.profile_pic ? (
                <img src={user.profile_pic} alt="" className="w-full h-full object-cover" />
            ) : (
                <span>{user?.name?.[0]?.toUpperCase() || "?"}</span>
            )}
        </div>
    );
}
